import { API_ENDPOINTS } from "../config/api";
import { ReferralTracker } from "../utils/referralTracker";
import {
  User,
  College,
  Essay,
  DatabaseCollege,
  DatabaseEssay,
  UICollege,
  UIEssay,
  EssayShare,
  SharedEssay,
  DatabaseEssayShare,
  PublicShareLink,
  SharedEssayPublic,
  DatabasePublicShareLink,
} from "../types/database";

export class DatabaseService {
  // User operations
  static async getCurrentUser(userId?: string): Promise<User | null> {
    try {
      if (!userId) return null;

      const response = await fetch(API_ENDPOINTS.getUser(userId));

      if (!response.ok) {
        if (response.status === 404) {
          return null;
        }
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error("Error getting current user:", error);
      return null;
    }
  }

  static async ensureUserExists(authUser?: {
    id: string;
    email: string;
    name?: string;
  }): Promise<User | null> {
    try {
      if (!authUser) return null;

      // Try to get existing user first
      const existingUser = await this.getCurrentUser(authUser.id);
      if (existingUser) {
        return existingUser;
      }

      // Check for referral code using the referral tracker
      const referralCode = ReferralTracker.getStoredReferralCode();

      // Create new user if they don't exist
      const response = await fetch(API_ENDPOINTS.createUser, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          id: authUser.id,
          email: authUser.email,
          name: authUser.name || authUser.email?.split("@")[0],
          is_active: true,
          referral_code: referralCode || undefined,
        }),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const newUser = await response.json();

      // Store referral code in user record but don't process signup yet
      // Processing will happen after onboarding completion
      if (referralCode) {
        console.log(
          "🔗 Referral code detected during user creation:",
          referralCode
        );
        console.log(
          "📝 Referral will be processed after onboarding completion"
        );
      }

      return newUser;
    } catch (error) {
      console.error("Error ensuring user exists:", error);
      return null;
    }
  }

  static async markUserAsOnboarded(userId: string): Promise<User | null> {
    try {
      const response = await fetch(API_ENDPOINTS.updateUser(userId), {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          onboarded: true,
        }),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const updatedUser = await response.json();

      // Process referral signup after onboarding completion
      const referralCode = ReferralTracker.getStoredReferralCode();
      if (referralCode) {
        try {
          console.log("🎓 Onboarding complete! Processing referral signup:", {
            referralCode,
            userId,
            endpoint: API_ENDPOINTS.processReferralSignup,
          });

          const referralResponse = await fetch(
            API_ENDPOINTS.processReferralSignup,
            {
              method: "POST",
              headers: {
                "Content-Type": "application/json",
              },
              body: JSON.stringify({
                referral_code: referralCode,
                user_id: userId,
              }),
            }
          );

          const referralResult = await referralResponse.json();
          console.log("🎓 Referral processing result:", referralResult);

          if (!referralResponse.ok) {
            console.error("❌ Referral processing failed:", referralResult);
          } else {
            console.log(
              "✅ Referral signup processed successfully after onboarding!"
            );
          }

          // Clear the referral code after processing attempt
          ReferralTracker.clearStoredReferralCode();
        } catch (referralError) {
          console.error(
            "Error processing referral signup after onboarding:",
            referralError
          );
          // Don't fail onboarding completion if referral processing fails
        }
      }

      return updatedUser;
    } catch (error) {
      console.error("Error marking user as onboarded:", error);
      return null;
    }
  }

  static async updateUser(
    userId: string,
    updates: Record<string, any>
  ): Promise<User | null> {
    try {
      const response = await fetch(API_ENDPOINTS.updateUser(userId), {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(updates),
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error(`HTTP ${response.status} error:`, errorText);
        throw new Error(
          `HTTP error! status: ${response.status}, details: ${errorText}`
        );
      }

      return await response.json();
    } catch (error) {
      console.error("Error updating user:", error);
      return null;
    }
  }

  // Profile image operations
  static async uploadProfileImage(
    userId: string,
    file: File
  ): Promise<{
    success: boolean;
    signed_url?: string;
    profile_image_key?: string;
    message?: string;
  } | null> {
    try {
      const formData = new FormData();
      formData.append("file", file);

      const response = await fetch(API_ENDPOINTS.uploadProfileImage(userId), {
        method: "POST",
        body: formData,
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(
          errorData.detail || `HTTP error! status: ${response.status}`
        );
      }

      return await response.json();
    } catch (error) {
      console.error("Error uploading profile image:", error);
      throw error;
    }
  }

  static async deleteProfileImage(userId: string): Promise<boolean> {
    try {
      const response = await fetch(API_ENDPOINTS.deleteProfileImage(userId), {
        method: "DELETE",
      });

      return response.ok;
    } catch (error) {
      console.error("Error deleting profile image:", error);
      return false;
    }
  }

  static async getUserSubscriptionPlan(userId: string): Promise<string | null> {
    try {
      const response = await fetch(API_ENDPOINTS.getUserSubscription(userId));

      if (!response.ok) {
        console.error(
          "Error fetching user subscription plan:",
          response.status
        );
        return "free";
      }

      const data = await response.json();
      return data.subscription_plan || "free";
    } catch (error) {
      console.error("Error getting user subscription plan:", error);
      return "free";
    }
  }

  static async updateUserSubscriptionPlan(
    userId: string,
    plan: "free" | "pro" | "enterprise"
  ): Promise<boolean> {
    try {
      // This would need a PUT /api/users/{userId}/subscription endpoint
      console.warn("User subscription updates not implemented yet");
      return false;
    } catch (error) {
      console.error("Error updating user subscription plan:", error);
      return false;
    }
  }

  // Usage tracking methods
  static async getUserAnalysisCount(userId: string): Promise<number> {
    try {
      const user = await this.getCurrentUser(userId);
      return user?.analysis_count || 0;
    } catch (error) {
      console.error("Error getting user analysis count:", error);
      return 0;
    }
  }

  static async getAllUsers(): Promise<User[]> {
    try {
      const response = await fetch(API_ENDPOINTS.getAllUsers);

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error("Error getting all users:", error);
      return [];
    }
  }

  // Reviewer-specific operations
  static async getActiveReviewers(): Promise<User[]> {
    try {
      console.log("🔍 Fetching active reviewers from backend API...");

      const response = await fetch(API_ENDPOINTS.getActiveReviewers);

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      console.log("✅ Raw reviewer data from API:", data);

      // Transform the data to match our User interface
      const reviewers: User[] =
        data?.map((reviewer: any) => {
          // Handle both view format and joined table format
          const reviewerProfile = reviewer.reviewer_profiles?.[0] || reviewer;

          return {
            id: reviewer.user_id || reviewer.id,
            email: reviewer.email,
            name: reviewer.name,
            is_active: true,
            roles: reviewer.roles || [],
            university: reviewer.university,
            major: reviewer.major,
            bio: reviewer.bio,
            age: reviewer.age,
            education_level: reviewer.education_level,
            profile_image_url: reviewer.profile_image_url,
            status: reviewer.status,
            university_email: reviewer.university_email || reviewer.email,
            onboarded: true,
            created_at: reviewer.created_at,
            updated_at: reviewer.updated_at,
            // Reviewer-specific fields
            is_available: reviewerProfile.is_available,
            years_of_experience: reviewerProfile.years_of_experience,
            total_reviews_completed: reviewerProfile.total_reviews_completed,
            average_rating: reviewerProfile.average_rating,
            specializations: reviewerProfile.specializations,
            essay_types: reviewerProfile.essay_types,
            target_schools: reviewerProfile.target_schools,
            university_verified: reviewerProfile.university_verified,
            response_time_hours: reviewerProfile.response_time_hours,
            completion_rate: reviewerProfile.completion_rate,
          };
        }) || [];

      console.log("🎯 Transformed reviewers:", reviewers);

      // If no real reviewers found, return mock data for development
      if (reviewers.length === 0) {
        console.warn("⚠️ No reviewers found in database, using mock data");
        return this.getMockReviewers();
      }

      console.log(`✅ Found ${reviewers.length} real reviewers!`);
      return reviewers;
    } catch (error) {
      console.error("💥 Error getting active reviewers:", error);
      // Return mock data for development
      return this.getMockReviewers();
    }
  }

  private static getMockReviewers(): User[] {
    return [
      {
        id: "reviewer1",
        email: "alex.johnson@stanford.edu",
        name: "Alex Johnson",
        is_active: true,
        roles: ["reviewer", "alum"],
        university: "Stanford University",
        major: "Computer Science",
        bio: "I'm a Stanford alum with experience in reviewing college applications. I've helped dozens of students get into top universities with compelling personal statements and technical essays.",
        age: 28,
        education_level: "masters",
        profile_image_url: "https://randomuser.me/api/portraits/men/32.jpg",
        status: "graduated",
        university_email: "alex.johnson@stanford.edu",
        onboarded: true,
        created_at: new Date(
          Date.now() - 365 * 24 * 60 * 60 * 1000
        ).toISOString(),
        updated_at: new Date().toISOString(),
      },
      {
        id: "reviewer2",
        email: "maya.patel@harvard.edu",
        name: "Maya Patel",
        is_active: true,
        roles: ["reviewer", "student"],
        university: "Harvard University",
        major: "English Literature",
        bio: "Current Harvard student passionate about helping others with their application essays. I specialize in personal statements and creative writing. My focus is on helping students find their unique voice.",
        age: 21,
        education_level: "bachelors",
        profile_image_url: "https://randomuser.me/api/portraits/women/44.jpg",
        status: "in_university",
        university_email: "maya.patel@harvard.edu",
        onboarded: true,
        created_at: new Date(
          Date.now() - 180 * 24 * 60 * 60 * 1000
        ).toISOString(),
        updated_at: new Date().toISOString(),
      },
      {
        id: "reviewer3",
        email: "david.kim@mit.edu",
        name: "David Kim",
        is_active: true,
        roles: ["reviewer", "alum"],
        university: "MIT",
        major: "Physics",
        bio: "MIT graduate with 5+ years of experience in college admissions consulting. I can help with STEM-focused applications and essays. Specialized in research essays and technical writing.",
        age: 32,
        education_level: "phd",
        profile_image_url: "https://randomuser.me/api/portraits/men/67.jpg",
        status: "graduated",
        university_email: "david.kim@mit.edu",
        onboarded: true,
        created_at: new Date(
          Date.now() - 1825 * 24 * 60 * 60 * 1000
        ).toISOString(),
        updated_at: new Date().toISOString(),
      },
      {
        id: "reviewer4",
        email: "sophia.rodriguez@columbia.edu",
        name: "Sophia Rodriguez",
        is_active: true,
        roles: ["reviewer", "student"],
        university: "Columbia University",
        major: "Psychology",
        bio: "Psychology major at Columbia with a passion for helping students tell their unique stories. I focus on helping applicants showcase their personal growth and overcome challenges in their essays.",
        age: 22,
        education_level: "bachelors",
        profile_image_url: "https://randomuser.me/api/portraits/women/33.jpg",
        status: "in_university",
        university_email: "sophia.rodriguez@columbia.edu",
        onboarded: true,
        created_at: new Date(
          Date.now() - 120 * 24 * 60 * 60 * 1000
        ).toISOString(),
        updated_at: new Date().toISOString(),
      },
      {
        id: "reviewer5",
        email: "james.wilson@yale.edu",
        name: "James Wilson",
        is_active: true,
        roles: ["reviewer", "alum"],
        university: "Yale University",
        major: "Economics",
        bio: "Yale alum working in finance. I help students craft compelling narratives for business and economics programs at top universities. Experienced with MBA and undergraduate business essays.",
        age: 29,
        education_level: "masters",
        profile_image_url: "https://randomuser.me/api/portraits/men/22.jpg",
        status: "graduated",
        university_email: "james.wilson@yale.edu",
        onboarded: true,
        created_at: new Date(
          Date.now() - 730 * 24 * 60 * 60 * 1000
        ).toISOString(),
        updated_at: new Date().toISOString(),
      },
      {
        id: "reviewer6",
        email: "emma.chen@princeton.edu",
        name: "Emma Chen",
        is_active: true,
        roles: ["reviewer", "alum"],
        university: "Princeton University",
        major: "International Relations",
        bio: "Princeton graduate specializing in international relations and public policy essays. I help students articulate their global perspectives and social impact goals.",
        age: 26,
        education_level: "bachelors",
        profile_image_url: "https://randomuser.me/api/portraits/women/67.jpg",
        status: "graduated",
        university_email: "emma.chen@princeton.edu",
        onboarded: true,
        created_at: new Date(
          Date.now() - 500 * 24 * 60 * 60 * 1000
        ).toISOString(),
        updated_at: new Date().toISOString(),
      },
      {
        id: "reviewer7",
        email: "marcus.thompson@duke.edu",
        name: "Marcus Thompson",
        is_active: true,
        roles: ["reviewer", "student"],
        university: "Duke University",
        major: "Biomedical Engineering",
        bio: "Duke student studying biomedical engineering. I'm passionate about helping pre-med and engineering students showcase their research and clinical experiences in their applications.",
        age: 20,
        education_level: "bachelors",
        profile_image_url: "https://randomuser.me/api/portraits/men/55.jpg",
        status: "in_university",
        university_email: "marcus.thompson@duke.edu",
        onboarded: true,
        created_at: new Date(
          Date.now() - 90 * 24 * 60 * 60 * 1000
        ).toISOString(),
        updated_at: new Date().toISOString(),
      },
      {
        id: "reviewer8",
        email: "lisa.wang@northwestern.edu",
        name: "Lisa Wang",
        is_active: true,
        roles: ["reviewer", "alum"],
        university: "Northwestern University",
        major: "Journalism",
        bio: "Northwestern journalism graduate with experience in media and communications. I help students craft compelling narratives and improve their storytelling skills for college essays.",
        age: 27,
        education_level: "masters",
        profile_image_url: "https://randomuser.me/api/portraits/women/89.jpg",
        status: "graduated",
        university_email: "lisa.wang@northwestern.edu",
        onboarded: true,
        created_at: new Date(
          Date.now() - 400 * 24 * 60 * 60 * 1000
        ).toISOString(),
        updated_at: new Date().toISOString(),
      },
    ];
  }

  /**
   * Helper function to add status-based role tags (student/alum) based on user status
   */
  static async updateRoleTagsBasedOnStatus(
    userId: string,
    status: "applying" | "in_university" | "graduated"
  ): Promise<boolean> {
    try {
      console.log("🏷️ Updating role tags based on status:", status);

      // Get current user data to check existing roles
      const currentUser = await this.getCurrentUser(userId);
      if (!currentUser) {
        console.error("❌ User not found");
        return false;
      }

      let updatedRoles = [...(currentUser.roles || [])];
      console.log("📋 Current roles before status update:", updatedRoles);

      // Update student/alum tags based on status
      if (status === "in_university" && !updatedRoles.includes("student")) {
        updatedRoles = updatedRoles.filter((r) => r !== "alum");
        if (!updatedRoles.includes("student")) {
          updatedRoles.push("student");
        }
        console.log("➕ Added student tag for in_university status");
      } else if (status === "graduated" && !updatedRoles.includes("alum")) {
        updatedRoles = updatedRoles.filter((r) => r !== "student");
        if (!updatedRoles.includes("alum")) {
          updatedRoles.push("alum");
        }
        console.log("➕ Added alum tag for graduated status");
      }

      // Only update if roles changed
      if (
        JSON.stringify(updatedRoles.sort()) ===
        JSON.stringify((currentUser.roles || []).sort())
      ) {
        console.log("✅ No role changes needed");
        return true;
      }

      console.log("📋 Updated roles after status update:", updatedRoles);

      // Update user roles
      const updatedUser = await this.updateUser(userId, {
        roles: updatedRoles,
      });

      if (!updatedUser) {
        console.error("❌ Failed to update user roles");
        return false;
      }

      console.log("✅ Successfully updated role tags based on status");
      return true;
    } catch (error) {
      console.error("Error updating role tags based on status:", error);
      return false;
    }
  }

  static async addReviewerRole(userId: string): Promise<boolean> {
    try {
      console.log("🎯 Adding reviewer role to user:", userId);

      // Get current user to check existing roles
      const currentUser = await this.getCurrentUser(userId);
      if (!currentUser) {
        console.error("❌ User not found");
        return false;
      }

      const currentRoles = currentUser.roles || [];
      console.log("📋 Current user roles:", currentRoles);

      // Add reviewer role if not already present
      let updatedRoles = currentRoles;
      if (!currentRoles.includes("reviewer")) {
        updatedRoles = [...currentRoles, "reviewer"];
        console.log("➕ Adding reviewer role. New roles:", updatedRoles);
      } else {
        console.log("✅ User already has reviewer role");
        return true;
      }

      // Update user roles using the existing updateUser method
      const updatedUser = await this.updateUser(userId, {
        roles: updatedRoles,
      });

      if (!updatedUser) {
        console.error("❌ Failed to update user roles");
        return false;
      }

      console.log("✅ Successfully added reviewer role");
      return true;
    } catch (error) {
      console.error("Error adding reviewer role:", error);
      return false;
    }
  }

  static async createReviewerProfile(
    userId: string,
    profileData: any
  ): Promise<boolean> {
    try {
      console.log(
        "🎯 Creating reviewer profile for user:",
        userId,
        "with data:",
        profileData
      );

      const response = await fetch(API_ENDPOINTS.createReviewerProfile, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          user_id: userId,
          is_available: true,
          years_of_experience: profileData.years_of_experience || 0,
          ...profileData,
        }),
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error("Failed to create reviewer profile:", errorText);
        return false;
      }

      console.log("✅ Successfully created reviewer profile via API");
      return true;
    } catch (error) {
      console.error("Error creating reviewer profile:", error);
      return false;
    }
  }

  static async updateReviewerProfile(
    userId: string,
    updates: any
  ): Promise<boolean> {
    try {
      console.log(
        "🔧 Updating reviewer profile for user:",
        userId,
        "with updates:",
        updates
      );

      const response = await fetch(
        API_ENDPOINTS.updateReviewerProfile(userId),
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(updates),
        }
      );

      if (!response.ok) {
        const errorText = await response.text();
        console.error("Failed to update reviewer profile:", errorText);
        return false;
      }

      console.log("✅ Successfully updated reviewer profile via API");
      return true;
    } catch (error) {
      console.error("Error updating reviewer profile:", error);
      return false;
    }
  }

  static async getReviewerProfile(userId: string): Promise<any | null> {
    try {
      // Try to call backend API
      const response = await fetch(
        `${API_ENDPOINTS.base}/reviewer-profiles/${userId}`
      );

      if (!response.ok) {
        console.warn("Backend API not available for getting reviewer profile");
        return null;
      }

      return await response.json();
    } catch (error) {
      console.error("Error getting reviewer profile:", error);
      return null;
    }
  }

  static async incrementUserAnalysisCount(userId: string): Promise<boolean> {
    try {
      // This is handled by the backend during analysis
      console.warn("Analysis count increment is handled by backend");
      return true;
    } catch (error) {
      console.error("Error incrementing user analysis count:", error);
      return false;
    }
  }

  static async checkUserCanAnalyze(userId: string): Promise<{
    canAnalyze: boolean;
    usageCount: number;
    subscriptionPlan: string;
  }> {
    try {
      const user = await this.getCurrentUser(userId);

      if (!user) {
        return { canAnalyze: false, usageCount: 0, subscriptionPlan: "free" };
      }

      const usageCount = user.analysis_count || 0;
      const subscriptionPlan = user.subscription_plan || "free";

      // Pro users can always analyze
      if (subscriptionPlan === "pro") {
        return { canAnalyze: true, usageCount, subscriptionPlan };
      }

      // Free users are limited to 5 analyses
      const canAnalyze = usageCount < 5;
      return { canAnalyze, usageCount, subscriptionPlan };
    } catch (error) {
      console.error("Error checking user analysis eligibility:", error);
      return { canAnalyze: false, usageCount: 0, subscriptionPlan: "free" };
    }
  }

  // College operations
  static async getUserColleges(userId: string): Promise<UICollege[]> {
    try {
      const response = await fetch(API_ENDPOINTS.getUserColleges(userId));

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      // Transform to UI format
      return data.map((college: any) => ({
        id: college.id,
        name: college.name,
        essayCount: 0, // Would need to calculate this
        lastModified: new Date(college.updated_at).toISOString().split("T")[0],
      }));
    } catch (error) {
      console.error("Error getting user colleges:", error);
      return [];
    }
  }

  static async getCollege(collegeId: string): Promise<UICollege | null> {
    try {
      // This would need a GET /api/colleges/{collegeId} endpoint
      console.warn("Individual college fetch not implemented yet");
      return null;
    } catch (error) {
      console.error("Error getting college:", error);
      return null;
    }
  }

  static async createCollege(
    name: string,
    userId: string
  ): Promise<College | null> {
    try {
      const response = await fetch(API_ENDPOINTS.createCollege, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          name: name.trim(),
          user_id: userId,
        }),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error("Error creating college:", error);
      return null;
    }
  }

  static async updateCollege(
    collegeId: string,
    updates: Partial<DatabaseCollege>
  ): Promise<College | null> {
    try {
      const response = await fetch(API_ENDPOINTS.updateCollege(collegeId), {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(updates),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error("Error updating college:", error);
      return null;
    }
  }

  static async deleteCollege(collegeId: string): Promise<boolean> {
    try {
      const response = await fetch(API_ENDPOINTS.deleteCollege(collegeId), {
        method: "DELETE",
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return data.success;
    } catch (error) {
      console.error("Error deleting college:", error);
      return false;
    }
  }

  // University operations (uses universities table)
  static async getAllUniversityNames(): Promise<string[]> {
    try {
      const response = await fetch(API_ENDPOINTS.getAllUniversityNames);

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error("Error fetching university names:", error);
      return [];
    }
  }

  static async getUniversity(collegeName: string): Promise<any | null> {
    try {
      const response = await fetch(API_ENDPOINTS.getUniversity(collegeName));

      if (!response.ok) {
        if (response.status === 404) {
          return null;
        }
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error("Error fetching university:", error);
      return null;
    }
  }

  // Essay operations
  static async getUserEssays(userId: string): Promise<UIEssay[]> {
    try {
      const response = await fetch(API_ENDPOINTS.getUserEssays(userId));

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      // Transform to UI format
      return data.map((essay: any) => ({
        id: essay.id,
        title: essay.title,
        lastModified: new Date(essay.updated_at).toISOString().split("T")[0],
        status: essay.status,
        collegeId: essay.college_id || undefined,
        wordLimit: essay.word_limit || undefined,
        prompt: essay.prompt || undefined,
      }));
    } catch (error) {
      console.error("Error getting user essays:", error);
      return [];
    }
  }

  static async getEssay(essayId: string): Promise<Essay | null> {
    try {
      const response = await fetch(API_ENDPOINTS.getEssay(essayId));

      if (!response.ok) {
        if (response.status === 404) {
          return null;
        }
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error("Error getting essay:", error);
      return null;
    }
  }

  static async createEssay(
    title: string,
    userId: string,
    collegeId?: string,
    content?: string,
    prompt?: string,
    wordLimit?: number
  ): Promise<Essay | null> {
    try {
      const response = await fetch(API_ENDPOINTS.createEssay, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          title: title.trim(),
          content: content || "",
          prompt: prompt || "",
          word_limit: wordLimit || null,
          user_id: userId,
          college_id: collegeId || null,
          status: "draft",
        }),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error("Error creating essay:", error);
      return null;
    }
  }

  static async updateEssay(
    essayId: string,
    updates: Partial<DatabaseEssay>
  ): Promise<Essay | null> {
    try {
      const response = await fetch(API_ENDPOINTS.updateEssay(essayId), {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(updates),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error("Error updating essay:", error);
      return null;
    }
  }

  static async deleteEssay(essayId: string): Promise<boolean> {
    try {
      const response = await fetch(API_ENDPOINTS.deleteEssay(essayId), {
        method: "DELETE",
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return data.success;
    } catch (error) {
      console.error("Error deleting essay:", error);
      return false;
    }
  }

  // Get essays by college
  static async getCollegeEssays(collegeId: string): Promise<UIEssay[]> {
    try {
      // This would need a GET /api/colleges/{collegeId}/essays endpoint
      console.warn("College essays fetch not implemented yet");
      return [];
    } catch (error) {
      console.error("Error getting college essays:", error);
      return [];
    }
  }

  // Get individual essays (not associated with a college)
  static async getIndividualEssays(userId: string): Promise<UIEssay[]> {
    try {
      const essays = await this.getUserEssays(userId);
      return essays.filter((essay) => !essay.collegeId);
    } catch (error) {
      console.error("Error getting individual essays:", error);
      return [];
    }
  }

  // Essay sharing operations
  static async shareEssay(
    essayId: string,
    sharedByUserId: string,
    emails: string[],
    permissionLevel: "read" | "comment" | "edit"
  ): Promise<EssayShare[]> {
    try {
      const response = await fetch(API_ENDPOINTS.shareEssay, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          essay_id: essayId,
          shared_by_user_id: sharedByUserId,
          emails: emails,
          permission_level: permissionLevel,
        }),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error("Error sharing essay:", error);
      return [];
    }
  }

  static async getUserSharedEssays(userId: string): Promise<SharedEssay[]> {
    try {
      const response = await fetch(API_ENDPOINTS.getUserSharedEssays(userId));

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error("Error getting user shared essays:", error);
      return [];
    }
  }

  static async getEssayShares(essayId: string): Promise<EssayShare[]> {
    try {
      const response = await fetch(API_ENDPOINTS.getEssayShares(essayId));

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error("Error getting essay shares:", error);
      return [];
    }
  }

  static async updateEssayShare(
    shareId: string,
    updates: Partial<DatabaseEssayShare>
  ): Promise<EssayShare | null> {
    try {
      const response = await fetch(API_ENDPOINTS.updateEssayShare(shareId), {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(updates),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error("Error updating essay share:", error);
      return null;
    }
  }

  static async deleteEssayShare(shareId: string): Promise<boolean> {
    try {
      const response = await fetch(API_ENDPOINTS.deleteEssayShare(shareId), {
        method: "DELETE",
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return data.success;
    } catch (error) {
      console.error("Error deleting essay share:", error);
      return false;
    }
  }

  // Check if user has permission to access an essay
  static async getUserEssayPermission(
    essayId: string,
    userId: string
  ): Promise<"read" | "comment" | "edit" | "owner" | null> {
    try {
      const response = await fetch(
        API_ENDPOINTS.getUserEssayPermission(essayId, userId)
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return data.permission;
    } catch (error) {
      console.error("Error checking essay permission:", error);
      return null;
    }
  }

  // College Planning Operations
  static async getPlanningColleges(userId: string): Promise<any[]> {
    try {
      const response = await fetch(API_ENDPOINTS.getPlanningColleges(userId));

      if (!response.ok) {
        console.warn(
          "Backend API not available for planning colleges, using empty array"
        );
        return [];
      }

      const result = await response.json();
      return this.toCamelCase(result);
    } catch (error) {
      console.error("Error getting planning colleges:", error);
      return [];
    }
  }

  // Helper function to convert camelCase to snake_case
  private static toSnakeCase(obj: any): any {
    if (Array.isArray(obj)) {
      return obj.map((item) => this.toSnakeCase(item));
    } else if (obj !== null && typeof obj === "object") {
      const result: any = {};
      for (const [key, value] of Object.entries(obj)) {
        const snakeKey = key.replace(
          /[A-Z]/g,
          (letter) => `_${letter.toLowerCase()}`
        );
        result[snakeKey] = this.toSnakeCase(value);
      }
      return result;
    }
    return obj;
  }

  // Helper function to convert snake_case to camelCase
  private static toCamelCase(obj: any): any {
    if (Array.isArray(obj)) {
      return obj.map((item) => this.toCamelCase(item));
    } else if (obj !== null && typeof obj === "object") {
      const result: any = {};
      for (const [key, value] of Object.entries(obj)) {
        const camelKey = key.replace(/_([a-z])/g, (_, letter) =>
          letter.toUpperCase()
        );
        result[camelKey] = this.toCamelCase(value);
      }
      return result;
    }
    return obj;
  }

  static async createPlanningCollege(
    userId: string,
    collegeData: any
  ): Promise<any | null> {
    try {
      const snakeCaseData = this.toSnakeCase(collegeData);

      const response = await fetch(API_ENDPOINTS.createPlanningCollege, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          user_id: userId,
          ...snakeCaseData,
        }),
      });

      if (!response.ok) {
        console.warn("Backend API not available for creating planning college");
        return null;
      }

      const result = await response.json();
      return this.toCamelCase(result);
    } catch (error) {
      console.error("Error creating planning college:", error);
      return null;
    }
  }

  static async updatePlanningCollege(
    collegeId: string,
    updates: any
  ): Promise<any> {
    const response = await fetch(
      API_ENDPOINTS.updatePlanningCollege(collegeId),
      {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(updates),
      }
    );

    if (!response.ok) {
      const errorText = await response.text();
      console.error("Error updating planning college:", response.status, errorText);
      throw new Error(`Failed to update college: ${response.status}`);
    }

    return await response.json();
  }

  static async deletePlanningCollege(collegeId: string): Promise<boolean> {
    try {
      const response = await fetch(
        API_ENDPOINTS.deletePlanningCollege(collegeId),
        {
          method: "DELETE",
        }
      );

      if (!response.ok) {
        console.warn("Backend API not available for deleting planning college");
        return false;
      }

      return true;
    } catch (error) {
      console.error("Error deleting planning college:", error);
      return false;
    }
  }

  // Milestone Operations
  static async getPlanningMilestones(userId: string): Promise<any[]> {
    try {
      const response = await fetch(API_ENDPOINTS.getPlanningMilestones(userId));

      if (!response.ok) {
        console.warn(
          "Backend API not available for planning milestones, using empty array"
        );
        return [];
      }

      const result = await response.json();
      return this.toCamelCase(result);
    } catch (error) {
      console.error("Error getting planning milestones:", error);
      return [];
    }
  }

  static async createPlanningMilestone(
    userId: string,
    milestoneData: any
  ): Promise<any | null> {
    try {
      const snakeCaseData = this.toSnakeCase(milestoneData);

      const response = await fetch(API_ENDPOINTS.createPlanningMilestone, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          user_id: userId,
          ...snakeCaseData,
        }),
      });

      if (!response.ok) {
        console.warn(
          "Backend API not available for creating planning milestone"
        );
        return null;
      }

      const result = await response.json();
      return this.toCamelCase(result);
    } catch (error) {
      console.error("Error creating planning milestone:", error);
      return null;
    }
  }

  static async updatePlanningMilestone(
    milestoneId: string,
    updates: any
  ): Promise<any | null> {
    try {
      const snakeCaseUpdates = this.toSnakeCase(updates);

      const response = await fetch(
        API_ENDPOINTS.updatePlanningMilestone(milestoneId),
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(snakeCaseUpdates),
        }
      );

      if (!response.ok) {
        console.warn(
          "Backend API not available for updating planning milestone"
        );
        return null;
      }

      const result = await response.json();
      return this.toCamelCase(result);
    } catch (error) {
      console.error("Error updating planning milestone:", error);
      return null;
    }
  }

  static async deletePlanningMilestone(milestoneId: string): Promise<boolean> {
    try {
      const response = await fetch(
        API_ENDPOINTS.deletePlanningMilestone(milestoneId),
        {
          method: "DELETE",
        }
      );

      if (!response.ok) {
        console.warn(
          "Backend API not available for deleting planning milestone"
        );
        return false;
      }

      return true;
    } catch (error) {
      console.error("Error deleting planning milestone:", error);
      return false;
    }
  }

  // Calendar Event Operations
  static async getPlanningEvents(userId: string): Promise<any[]> {
    try {
      const response = await fetch(API_ENDPOINTS.getPlanningEvents(userId));

      if (!response.ok) {
        console.warn(
          "Backend API not available for planning events, using empty array"
        );
        return [];
      }

      const result = await response.json();
      return this.toCamelCase(result);
    } catch (error) {
      console.error("Error getting planning events:", error);
      return [];
    }
  }

  static async createPlanningEvent(
    userId: string,
    eventData: any
  ): Promise<any | null> {
    try {
      const response = await fetch(API_ENDPOINTS.createPlanningEvent, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          user_id: userId,
          ...this.toSnakeCase(eventData),
        }),
      });

      if (!response.ok) {
        console.warn("Backend API not available for creating planning event");
        return null;
      }

      const result = await response.json();
      return this.toCamelCase(result);
    } catch (error) {
      console.error("Error creating planning event:", error);
      return null;
    }
  }

  static async updatePlanningEvent(
    eventId: string,
    updates: any
  ): Promise<any | null> {
    try {
      const snakeCaseUpdates = this.toSnakeCase(updates);

      const response = await fetch(API_ENDPOINTS.updatePlanningEvent(eventId), {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(snakeCaseUpdates),
      });

      if (!response.ok) {
        console.warn("Backend API not available for updating planning event");
        return null;
      }

      const result = await response.json();
      return this.toCamelCase(result);
    } catch (error) {
      console.error("Error updating planning event:", error);
      return null;
    }
  }

  static async deletePlanningEvent(eventId: string): Promise<boolean> {
    try {
      const response = await fetch(API_ENDPOINTS.deletePlanningEvent(eventId), {
        method: "DELETE",
      });

      if (!response.ok) {
        console.warn("Backend API not available for deleting planning event");
        return false;
      }

      return true;
    } catch (error) {
      console.error("Error deleting planning event:", error);
      return false;
    }
  }

  // Helper to create deadline events for a college
  static async createCollegeDeadlineEvents(
    userId: string,
    collegeName: string,
    deadlines: {
      ea_deadline?: string;
      ed1_deadline?: string;
      ed2_deadline?: string;
      rd_deadline?: string;
    }
  ): Promise<void> {
    const deadlineTypes = [
      { key: "ea_deadline", label: "Early Action" },
      { key: "ed1_deadline", label: "Early Decision I" },
      { key: "ed2_deadline", label: "Early Decision II" },
      { key: "rd_deadline", label: "Regular Decision" },
    ] as const;

    for (const { key, label } of deadlineTypes) {
      const dateStr = deadlines[key];
      if (dateStr) {
        try {
          // Parse the date - handle various formats
          const date = new Date(dateStr);
          if (!isNaN(date.getTime())) {
            const formattedDate = date.toISOString().split("T")[0];
            await this.createPlanningEvent(userId, {
              title: `${collegeName} - ${label} Deadline`,
              description: `Application deadline for ${collegeName}`,
              date: formattedDate,
              type: "deadline",  // Use singular form to match database constraint
            });
          }
        } catch (error) {
          console.error(`Error creating deadline event for ${collegeName} ${label}:`, error);
        }
      }
    }
  }

  // Public sharing methods
  static async getEssayPublicShareLink(
    essayId: string,
    userId?: string
  ): Promise<PublicShareLink | null> {
    try {
      const response = await fetch(
        API_ENDPOINTS.getEssayPublicShareLink(essayId, userId)
      );

      if (!response.ok) {
        if (response.status === 404) {
          return null; // Link not found
        }
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error("Error getting essay public share link:", error);
      return null;
    }
  }

  static async getPublicShareLink(
    shareToken: string
  ): Promise<SharedEssayPublic | null> {
    try {
      const response = await fetch(API_ENDPOINTS.getPublicShare(shareToken));

      if (!response.ok) {
        if (response.status === 404) {
          return null; // Link not found or expired
        }
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error("Error getting public share link:", error);
      return null;
    }
  }

  static async toggleEssayPublicSharing(
    essayId: string,
    userId: string
  ): Promise<PublicShareLink | null> {
    try {
      const response = await fetch(
        API_ENDPOINTS.toggleEssayPublicSharing(essayId, userId),
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
          },
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error("Error toggling essay public sharing:", error);
      return null;
    }
  }

  static async incrementPublicShareView(shareToken: string): Promise<boolean> {
    try {
      const response = await fetch(
        API_ENDPOINTS.incrementPublicShareView(shareToken),
        {
          method: "POST",
        }
      );

      return response.ok;
    } catch (error) {
      console.error("Error incrementing public share view:", error);
      return false;
    }
  }

  // ============================================================================
  // Scholarship operations
  // ============================================================================

  static async searchScholarships(
    userId: string | null,
    searchPayload: any
  ): Promise<{ scholarships: any[]; total: number; count: number } | null> {
    try {
      const url = new URL(API_ENDPOINTS.searchScholarships);
      if (userId) {
        url.searchParams.append("user_id", userId);
      }

      const response = await fetch(url.toString(), {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(searchPayload),
      });

      if (!response.ok) {
        if (response.status === 401) {
          throw new Error("Authentication required");
        }
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error("Error searching scholarships:", error);
      throw error;
    }
  }

  static async getUserScholarships(
    userId: string,
    status?: string
  ): Promise<{ scholarships: any[]; count: number } | null> {
    try {
      const url = new URL(API_ENDPOINTS.getUserScholarships(userId));
      if (status) {
        url.searchParams.append("status", status);
      }

      const response = await fetch(url.toString());

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error("Error getting user scholarships:", error);
      return null;
    }
  }

  static async saveScholarship(
    scholarshipId: string,
    userId: string,
    data: {
      status: string;
      notes?: string;
      applied_date?: string;
    }
  ): Promise<any> {
    try {
      const url = new URL(API_ENDPOINTS.saveScholarship(scholarshipId));
      url.searchParams.append("user_id", userId);

      const response = await fetch(url.toString(), {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          scholarship_id: scholarshipId,
          ...data,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(
          errorData.detail || `HTTP error! status: ${response.status}`
        );
      }

      return await response.json();
    } catch (error) {
      console.error("Error saving scholarship:", error);
      throw error;
    }
  }

  static async updateScholarshipStatus(
    scholarshipId: string,
    userId: string,
    data: {
      status?: string;
      notes?: string;
      applied_date?: string;
    }
  ): Promise<any> {
    try {
      const url = new URL(API_ENDPOINTS.saveScholarship(scholarshipId));
      url.searchParams.append("user_id", userId);

      const response = await fetch(url.toString(), {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(data),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error("Error updating scholarship status:", error);
      throw error;
    }
  }

  static async unsaveScholarship(
    scholarshipId: string,
    userId: string
  ): Promise<boolean> {
    try {
      const url = new URL(API_ENDPOINTS.saveScholarship(scholarshipId));
      url.searchParams.append("user_id", userId);

      const response = await fetch(url.toString(), {
        method: "DELETE",
      });

      return response.ok;
    } catch (error) {
      console.error("Error unsaving scholarship:", error);
      return false;
    }
  }

  // University/College operations
  static async searchUniversities(searchPayload: {
    search_query?: string;
    state?: string;
    sat_act_required?: boolean;
    has_ea_deadline?: boolean;
    has_ed_deadline?: boolean;
    // Academic filters
    min_gpa?: number;
    max_gpa?: number;
    min_sat?: number;
    max_sat?: number;
    min_act?: number;
    max_act?: number;
    // Financial filters
    max_tuition?: number;
    max_in_state_tuition?: number;
    min_need_based_aid?: number;
    min_international_aid?: number;
    min_avg_aid_package?: number;
    max_application_fee?: number;
    // Campus filters
    institution_setting?: string[];
    max_enrollment?: number;
    limit?: number;
    offset?: number;
  }): Promise<{ universities: any[]; total: number; count: number } | null> {
    try {
      const response = await fetch(API_ENDPOINTS.searchUniversities, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(searchPayload),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error("Error searching universities:", error);
      throw error;
    }
  }

  // College save/unsave operations (uses planning_colleges)
  static async saveCollege(
    collegeName: string,
    userId: string,
    data: {
      notes?: string;
    } = {}
  ): Promise<any> {
    try {
      const url = new URL(API_ENDPOINTS.saveCollege(collegeName));
      url.searchParams.append("user_id", userId);
      if (data.notes) url.searchParams.append("notes", data.notes);

      const response = await fetch(url.toString(), {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(
          errorData.detail || `HTTP error! status: ${response.status}`
        );
      }

      return await response.json();
    } catch (error) {
      console.error("Error saving college:", error);
      throw error;
    }
  }

  static async unsaveCollege(
    collegeName: string,
    userId: string
  ): Promise<boolean> {
    try {
      const url = new URL(API_ENDPOINTS.saveCollege(collegeName));
      url.searchParams.append("user_id", userId);

      const response = await fetch(url.toString(), {
        method: "DELETE",
      });

      return response.ok;
    } catch (error) {
      console.error("Error unsaving college:", error);
      return false;
    }
  }

  static async getSavedColleges(
    userId: string
  ): Promise<{ colleges: any[]; count: number } | null> {
    try {
      const response = await fetch(API_ENDPOINTS.getSavedColleges(userId));

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error("Error getting saved colleges:", error);
      return null;
    }
  }
}
